import type { ReactNode } from 'react';

// Line icons for the service/about benefit lists. Stroke-based on a 24px grid,
// rendered at 22px and inheriting color via `currentColor` (callers pass
// `text-primary`). Shared <Svg> wrapper keeps the stroke style consistent.
type IconProps = { className?: string };

function Svg({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

/* — Floors & Doors — */

// Laminate / parquet planks — "Floor installation".
export function FloorIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="M3 9.7h18M3 14.3h18M10 5v4.7M15 9.7v4.6M8 14.3V19" />
    </Svg>
  );
}

// Three-step staircase — "Tiling on stairs".
export function StairsIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M4 20V16h5v-4h5V8h5v12z" />
    </Svg>
  );
}

// Closed door + floor line (skirting) + knob — "Doors and skirting".
export function DoorIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14" />
      <path d="M2 20h20" />
      <path d="M14 12v.01" />
    </Svg>
  );
}

/* — About / process — */

// Single person — the personal project manager guiding every stage.
export function ManagerIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Svg>
  );
}

// Group — dedicated specialists for each type of work.
export function SpecialistsIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  );
}

// Clipboard with a check — quality & deadline control.
export function ControlIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </Svg>
  );
}

// Wrench — construction standards / professional tools owned by the company.
export function ToolsIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </Svg>
  );
}

/* — Electrical & Plumbing — */

// Lightning bolt — safe electrical system.
export function BoltIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
    </Svg>
  );
}

// Water drop — leak protection.
export function DropletIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
    </Svg>
  );
}

// Document with lines — installation to an agreed plan.
export function PlanIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </Svg>
  );
}

/* — Tile Installation — */

// Set-square / right angle — perfect external corners.
export function CornerIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M5 5V19H19Z" />
      <path d="M9 19v-4H5" />
    </Svg>
  );
}

// Shield with a check — protection against cracks and detachment.
export function ShieldCheckIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  );
}

// Two water drops — custom sinks and shower trays.
export function DropletsIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 4.8 7 3.5c-.29 1.3-1.15 2.42-2.29 3.36S3 11.09 3 12.25c0 2.22 1.8 4.05 4 4.05z" />
      <path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97" />
    </Svg>
  );
}

/* — Painting Works — */

// Paint roller — premium painting quality.
export function RollerIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="3" y="4" width="12" height="6" rx="1.5" />
      <path d="M15 7h3v4h-6v3" />
      <rect x="9" y="14" width="6" height="6" rx="1" />
    </Svg>
  );
}

// Sparkles — decorative coatings (wallpaper & decorative plaster).
export function DecorIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M18 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
    </Svg>
  );
}

// Spirit level — surface preparation (perfectly even surface).
export function LevelIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="2" y="9" width="20" height="6" rx="1.5" />
      <circle cx="12" cy="12" r="1.4" />
      <path d="M7 12h.01M17 12h.01" />
    </Svg>
  );
}

// Award medal — quality backed by certification.
export function AwardIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="8" r="6" />
      <path d="M15.5 12.9 17 22l-5-3-5 3 1.5-9.1" />
    </Svg>
  );
}
